import { NgClass } from "@angular/common";
import { Component, Input, OnDestroy, OnInit, ViewChild, inject, ChangeDetectionStrategy } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { XAxisPlotLinesOptions } from "highcharts";

import { IndicatorNumberComponent } from "./indicator-number/indicator-number.component";
import { ApiService } from "src/app/api.service";
import { ChartingService } from "src/app/charting.service";
import { FlightViewer } from "src/app/flight-viewer";
import { DatetimePipe } from "src/app/pipes";
import { DvsChartComponent } from "src/app/shared/dvs-chart/dvs-chart.component";
import { APIResults, AssemblyConfig } from "src/app/sharedinterfaces";

@Component({
	selector: "app-flight-map",
	templateUrl: "./flight-map.component.html",
	styleUrls: ["./flight-map.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [NgClass, DvsChartComponent, IndicatorNumberComponent, FormsModule, DatetimePipe]
})
export class FlightMapComponent implements OnDestroy, OnInit {
	api = inject(ApiService);
	private charting = inject(ChartingService);
	private modalService = inject(NgbModal);

	@Input()
	asset: APIResults.FleetAsset;
	@Input()
	fleet: APIResults.Fleet;
	@Input()
	operation: APIResults.Operation;

	@ViewChild("scrubberChart", { static: true })
	scrubberChart: DvsChartComponent;

	@ViewChild("indicatorModal")
	public indicatorModal: any;

	private regimes: APIResults.RegimeIndex = {};

	public stateParams: AssemblyConfig.StateParameter[];
	public selectorParams: any[];
	public pathFrame: APIResults.FlightPathFrame;
	public scrubLine: XAxisPlotLinesOptions;

	public viewer: FlightViewer;

	public lockCamera = true;

	public scrubberOptions: Highcharts.Options = undefined;

	public loading = false;
	public error = undefined;

	ngOnInit() {
		const viewer = new FlightViewer(document.querySelector("#flightmap"));
		this.viewer = viewer;
		this.viewer.onPathFrame = (pathFrame) => {
			this.pathFrame = pathFrame;
			// Update the line on the scrubber chart
			if (this.scrubberChart && this.scrubLine) {
				this.scrubLine.value = this.pathFrame.ts * 1000;
				this.scrubberChart.UpdatePlotLine();
			}
		};
		this.operationChange();
	}

	async operationChange(): Promise<void> {
		this.scrubberOptions = undefined;
		this.scrubLine = undefined;
		this.loading = true;
		this.error = undefined;
		if (!this.stateParams) {
			// Load the available parameters for this asset
			const params = await this.api.GetParameters(this.asset.asset_id);
			const defaultParams = ["indicated airspeed", "heading", "altitude", "altitude rate", "ng", "l/h eng ng", "r/h eng ng", "nr", "torque", "xmsn torque"];
			if (params) {
				for (const param of params) {
					if (defaultParams.includes(param.name.toLowerCase())) {
						param.selected = true;
					}
				}
				this.stateParams = params;
			} else {
				this.stateParams = [];
			}
		}
		try {
			const selectedColumns = this.stateParams.filter(p => p.selected).map(p => p.id);
			const [path, exceedances] = await Promise.all([
				this.api.GetExpandedFlightPath(this.asset.asset_id, this.operation.operation_id, selectedColumns),
				this.api.GetExceedances(this.asset.asset_id, this.operation.operation_id),
			]);

			// Initialize the map and WebGL overlay
			await this.viewer.LoadPath(path, this.asset.override_heading);

			// Fetch the regimes to use later in the regime CI chart, if needed
			this.regimes = await this.api.GetRegimes(this.asset.asset_id);

			const altitudeSeries: any = {
				name: "Altitude",
				type: "line",
				zIndex: 1,
				data: path.map(point => [point.ts * 1000, point.altitude]),
				units: "ft",
				point: {
					events: {
						click: (evt) => {
							const progress = evt.point.index / path.length;
							this.viewer.SetProgress(progress);
						}
					}
				},
			};
			const timestamps = path.map(point => point.ts);

			const pointDetails = (point: Highcharts.Point) => {
				const frame = path[point.index];
				const regime_id = frame.regime_id;
				if (this.regimes && this.regimes[regime_id]) {
					return "<br/>Regime: " + this.regimes[regime_id].name;
				}
				return "";
			};
			const opt = this.charting.FlightTimeDefaults("", "", undefined, true, [altitudeSeries], timestamps, pointDetails, "Altitude", this.fleet ? this.fleet.time_offset_hours : 0);
			const xaxis: any = opt.xAxis;
			this.scrubLine = {
				value: path[0].ts * 1000,
				width: 1,
				color: "#fff",
				zIndex: 3,
			};

			const exceedanceBands = this.charting.GetExceedanceBands(exceedances.rows);
			xaxis.plotBands = [this.scrubLine, ...exceedanceBands];
			xaxis.crosshair = true;
			opt.chart.animation = false;
			opt.chart.zooming = {
				type: undefined,
			};
			opt.tooltip.shared = true;
			if (!opt.chart.events) opt.chart.events = {};
			opt.chart.events.click = () => {
				const hoverPoint = this.scrubberChart.chart.hoverPoint;
				if (hoverPoint) {
					const progress = hoverPoint.index / path.length;
					this.viewer.SetProgress(progress);
				}
			}
			this.scrubberOptions = opt;
		} catch (err) {
			console.error(err);
			this.error = "Unable to load flight data.";
		} finally {
			this.loading = false;
		}
	}

	ngOnDestroy() {
		if (this.viewer) this.viewer.Destroy();
	}

	lookupRegime(regime_id: number): string {
		if (this.regimes && this.regimes[regime_id]) {
			return this.regimes[regime_id].name;
		}
		return "";
	}

	ChooseIndicators() {
		this.selectorParams = this.stateParams.map(param => {
			return { id: param.id, selected: param.selected, name: param.name };
		});
		this.modalService.open(this.indicatorModal, { size: "lg" });
	}

	ApplyIndicators() {
		// Map selections back onto stateParams
		this.stateParams.forEach(param => {
			const selectorParam = this.selectorParams.find(p => p.id === param.id);
			if (selectorParam) {
				param.selected = selectorParam.selected;
			}
		});
		this.operationChange();
		this.modalService.dismissAll();
	}
}
